import axios, { AxiosInstance } from 'axios';

import { getApiHost } from '../config';
import { getAuthTokenFromStorage } from '../utils';
import {GENERAL_SERVER_ERROR, RESERVATION_NOT_FOUND} from './errorCodes';
import {reservationExpandParams} from './entities/reservation';

import dayjs from 'dayjs';
import { apiErrorHandler } from '../api/error-handler-old';
export class ReservationsService {

    constructor() {
        this.axiosClient = axios.create({
            baseURL: getApiHost()
        });

        this.axiosClient.interceptors.response.use(undefined, apiErrorHandler);
    }

    private axiosClient: AxiosInstance;

    private static get token(): string {
        return getAuthTokenFromStorage();
    }

    private reservationExpandParams(expandParams: reservationExpandParams): string {
        let params = '';
        Object.keys(expandParams).forEach((param: string) => params += `&$expand[]=${param}`);
        return params;
    }

    private async searchByPattern(pattern: string, expandParams: reservationExpandParams) {
        return await this.axiosClient.get(`reservations?${pattern}${this.reservationExpandParams(expandParams)}`, {
            headers: {
                'Authorization': `Bearer ${ReservationsService.token}`,
            }
        });
    }

    public async getAll() {
        try {
            const response = await this.axiosClient.get('reservations.json', {
                headers: {
                    'Authorization': `Bearer ${ReservationsService.token}`,
                }
            });
            return response.data;
            // @ts-ignore catch clause should not be typed and all the checks should be inside the clause
        } catch (e: AxiosError) {
            // :todo datadog logging replaces this
            console.error(e);
            return { errorCode: GENERAL_SERVER_ERROR };
        }
    }

    public async get(reservationId: string) {
        try {
            const expandParams: reservationExpandParams = {
                customer: true,
                property: true,
                reservationGuests: true,
                propertyUnit: true,
                notifications: true,
            };
            const response = await this.axiosClient.get(`reservations/${reservationId}?${this.reservationExpandParams(expandParams)}`, {
                headers: {
                    'Authorization': `Bearer ${ReservationsService.token}`,
                }
            });
            return response.data;
            // @ts-ignore catch clause should not be typed and all the checks should be inside the clause
        } catch (e: AxiosError) {
            // :todo datadog logging replaces this
            if (e?.response?.status === 404) {
                return { errorCode: RESERVATION_NOT_FOUND };
            }
            return { errorCode: GENERAL_SERVER_ERROR };
        }
    }

    public async getByDate(dateStart: dayjs.Dayjs, dateEnd?: dayjs.Dayjs) {
        try {
            const startDateBegin = dateStart.format('YYYY-MM-DDTHH:mm:ss');
            const startDateEnd = dateEnd?.format('YYYY-MM-DDTHH:mm:ss') || null;

            const expandParams: reservationExpandParams = {
                customer: true,
                property: true,
                orderItems: true,
                reservationNotes: true,
                reservationTags: true,
				'customer.customerNotes': true,
				'customer.customerNotes.createdByUser': true,
				'customer.customerNotes.updatedByUser': true,
				'customer.customerNotes.customerNoteTags': true,
                'customer.customerNotes.customerNoteTags.tag': true,
                'customer.customerTags': true,
                'customer.customerTags.tag': true,
            };
            const response = await this.searchByPattern(
                `status[$ne]=canceled&startDate[$gte]=${startDateBegin}${startDateEnd ? (`&startDate[$lt]=${startDateEnd}`) : ''}`,
                expandParams);

            return response.data;
            // @ts-ignore catch clause should not be typed and all the checks should be inside the clause
        } catch (e: AxiosError) {
            // :todo datadog logging replaces this
            console.error(e);
            if (e?.response?.status === 404) {
                return { errorCode: RESERVATION_NOT_FOUND };
            }
            return { errorCode: GENERAL_SERVER_ERROR };
        }
    }

    public async search(confirmationNumber: string) {
        try {
            const expandParams: reservationExpandParams = {
                customer: true,
                property: true,
                reservationGuests: true,
                propertyUnit: true,
                notifications: true,
            };
            const response = await this.searchByPattern(`confirmationNumber[$like]=${encodeURIComponent(confirmationNumber)}%`, expandParams);
            return response.data;
            // @ts-ignore catch clause should not be typed and all the checks should be inside the clause
        } catch (e: AxiosError) {
            // :todo datadog logging replaces this
            console.error(e);
            if (e?.response?.status === 404) {
                return { errorCode: RESERVATION_NOT_FOUND };
            }
            return { errorCode: GENERAL_SERVER_ERROR };
        }
    }
}
